import memoizeOne from 'memoize-one';
import { performGetRequest } from '@atlassian/jira-fetch';
import type { BillingInformationCCP } from './types';

const fetchEntitlementDetails = (entitlementId: string) =>
	performGetRequest(`/gateway/api/ccp/api/v2/entitlements/${entitlementId}/details`, {
		headers: {
			'Content-Type': 'application/json',
		},
	});

const fetchBillingAdmins = (transactionAccountId: string) =>
	performGetRequest('/gateway/api/ccp/api/v1/billing-admins', {
		headers: {
			'Content-Type': 'application/json',
			'X-transaction-account': transactionAccountId,
		},
	});

const fetchInvoiceGroupStatus = (transactionAccountId: string, invoiceGroupId: string) =>
	performGetRequest(`/gateway/api/ccp/api/v1/invoice-groups/${invoiceGroupId}/status`, {
		headers: {
			'Content-Type': 'application/json',
			'X-transaction-account': transactionAccountId,
		},
	});

const memoizedFetchEntitlementsDetails = memoizeOne(fetchEntitlementDetails);
const memoizedFetchInvoiceGroupsStatus = memoizeOne(fetchInvoiceGroupStatus);
const memoizedFetchBillingAdmins = memoizeOne(fetchBillingAdmins);

export const getBillingInformationCCP = async (
	entitlementId: string,
): Promise<BillingInformationCCP> => {
	// get the entitlement details
	const entitlementDetails = await memoizedFetchEntitlementsDetails(entitlementId);

	const transactionAccountId = entitlementDetails?.transactionAccountId;
	const invoiceGroupId = entitlementDetails?.subscriptionv2?.accountDetails?.invoiceGroupId;

	if (!transactionAccountId) throw new Error('empty transactionAccountId');
	if (!invoiceGroupId) throw new Error('empty invoiceGroupId');

	const invoiceGroupStatusPromise = memoizedFetchInvoiceGroupsStatus(
		transactionAccountId,
		invoiceGroupId,
	);

	const billingAdminsPromise = memoizedFetchBillingAdmins(transactionAccountId);

	// Invoiceability and billing admin checks could happen in parallel to improve pill performance
	const [invoiceGroupStatusResponse, billingAdminsResponse] = await Promise.all([
		invoiceGroupStatusPromise,
		billingAdminsPromise,
	]);

	const invoiceable = !!invoiceGroupStatusResponse?.invoiceable;
	const billingAdmins = billingAdminsResponse?.data || null;

	return {
		transactionAccountId,
		invoiceGroupId,
		invoiceable,
		billingAdmins,
	};
};
